// Shared AI-crawler detection. Client-safe (pure string matching) so the
// admin panel can reuse the same canonical bot list the server logs with.

/** Claimed bot name -> User-Agent tokens. Matching does NOT verify identity. */
export const AI_CRAWLER_SIGNATURES: ReadonlyArray<{ bot: string; tokens: readonly string[] }> = [
  { bot: "GPTBot", tokens: ["gptbot"] },
  { bot: "OAI-SearchBot", tokens: ["oai-searchbot"] },
  { bot: "ChatGPT-User", tokens: ["chatgpt-user"] },
  { bot: "ClaudeBot", tokens: ["claudebot"] },
  { bot: "Claude-Web", tokens: ["claude-web"] },
  { bot: "anthropic-ai", tokens: ["anthropic-ai"] },
  { bot: "PerplexityBot", tokens: ["perplexitybot"] },
  { bot: "Perplexity-User", tokens: ["perplexity-user"] },
  // Google-Extended and Applebot-Extended are robots policy tokens,
  // not separate HTTP crawlers. Historical claims remain in the database.
  { bot: "GoogleOther", tokens: ["googleother"] },
  { bot: "DeepSeekBot", tokens: ["deepseekbot"] },
  { bot: "MistralAI-User", tokens: ["mistralai-user"] },
  { bot: "YouBot", tokens: ["youbot"] },
  { bot: "Timpibot", tokens: ["timpibot"] },
  { bot: "Diffbot", tokens: ["diffbot"] },
  { bot: "Firecrawl", tokens: ["firecrawlagent", "firecrawl"] },
  { bot: "Tavily", tokens: ["tavilybot"] },
  { bot: "ExaBot", tokens: ["exabot"] },
  { bot: "Amazonbot", tokens: ["amazonbot"] },
  { bot: "meta-externalagent", tokens: ["meta-externalagent", "meta-externalfetcher"] },
  { bot: "DuckAssistBot", tokens: ["duckassistbot"] },
  { bot: "cohere-ai", tokens: ["cohere-ai", "cohere-training-data-crawler"] },
  { bot: "CCBot", tokens: ["ccbot"] },
  { bot: "Bytespider", tokens: ["bytespider"] },
];

export const AI_CRAWLER_NAMES = AI_CRAWLER_SIGNATURES.map((s) => s.bot);

/**
 * Returns the canonical bot name for a user-agent string, or null when the
 * request does not claim a recognised AI crawler. Never use for allowlisting.
 *
 * Order matters: the more specific tokens are listed first so
 * "Claude-Web" is never swallowed by a broader match.
 */
export function detectAiCrawler(userAgent: string | null | undefined): string | null {
  if (!userAgent) return null;
  const ua = userAgent.toLowerCase();
  for (const sig of AI_CRAWLER_SIGNATURES) {
    if (sig.tokens.some((t) => ua.includes(t))) return sig.bot;
  }
  return null;
}

/**
 * Bots that fetch a page *because a person just asked an assistant something*
 * (as opposed to scheduled index/training crawls). Their hits are the closest
 * proxy we have to "an AI was answering a question and reached for this page".
 */
export const LIVE_FETCH_BOTS: ReadonlySet<string> = new Set([
  "ChatGPT-User",
  "Perplexity-User",
  "Claude-Web",
  "OAI-SearchBot",
  "DuckAssistBot",
  "MistralAI-User",
]);

export function isLiveFetchBot(bot: string): boolean {
  return LIVE_FETCH_BOTS.has(bot);
}

/** Referrer host fragments -> the assistant a human arrived from. */
export const AI_REFERRER_SIGNATURES: ReadonlyArray<{ assistant: string; hosts: readonly string[] }> =
  [
    { assistant: "ChatGPT", hosts: ["chatgpt.com", "chat.openai.com", "openai.com"] },
    { assistant: "Perplexity", hosts: ["perplexity.ai"] },
    { assistant: "Copilot", hosts: ["copilot.microsoft.com", "bing.com/chat"] },
    { assistant: "Gemini", hosts: ["gemini.google.com", "bard.google.com"] },
    { assistant: "Claude", hosts: ["claude.ai"] },
    { assistant: "Meta AI", hosts: ["meta.ai"] },
    { assistant: "Grok", hosts: ["grok.com", "x.ai"] },
    { assistant: "DuckDuckGo AI", hosts: ["duck.ai"] },
    { assistant: "DeepSeek", hosts: ["chat.deepseek.com"] },
    { assistant: "Mistral", hosts: ["chat.mistral.ai"] },
    { assistant: "Poe", hosts: ["poe.com"] },
    { assistant: "You.com", hosts: ["you.com"] },
  ];

/**
 * Detects an AI-assistant referral from the document referrer and the landing
 * URL's utm/source parameters (assistants often tag outbound links).
 */
export function detectAiReferral(
  referrer: string | null | undefined,
  search: string | null | undefined,
): string | null {
  const hay = `${referrer ?? ""} ${search ?? ""}`.toLowerCase();
  if (!hay.trim()) return null;
  for (const sig of AI_REFERRER_SIGNATURES) {
    if (sig.hosts.some((h) => hay.includes(h))) return sig.assistant;
  }
  // Mobile apps often strip the referrer but keep a utm_source tag.
  try {
    const q = new URLSearchParams((search ?? "").replace(/^\?/, ""));
    const src = (q.get("utm_source") || q.get("ref") || "").toLowerCase().trim();
    const tags: Record<string, string> = {
      chatgpt: "ChatGPT", openai: "ChatGPT", perplexity: "Perplexity", claude: "Claude",
      copilot: "Copilot", gemini: "Gemini", deepseek: "DeepSeek", mistral: "Mistral", grok: "Grok",
    };
    for (const [tag, name] of Object.entries(tags)) if (src.startsWith(tag)) return name;
  } catch {
    /* ignore */
  }
  return null;
}

/** Question-ish text some assistants pass through on outbound links. */
export const QUESTION_PARAM_KEYS = ["q", "query", "question", "prompt", "utm_term", "s"] as const;

export function extractQuestionParam(search: string | null | undefined): string | null {
  if (!search) return null;
  try {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    for (const key of QUESTION_PARAM_KEYS) {
      const v = params.get(key);
      if (v && v.trim().length > 2) return v.trim().slice(0, 300);
    }
  } catch {
    /* ignore */
  }
  return null;
}
